/**
 * PackageManagement Page
 * Main container for package management with search, grid layout, and empty state
 */

import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Unstable_Grid2 as Grid } from '@mui/material';
import { Plus, Package as PackageIcon } from 'lucide-react';
import { useSnackbar } from 'notistack';
import {
  usePackages,
  useTogglePackageStatus,
  useCreatePackage,
  useUpdatePackage,
  CreatePackageInput,
  UpdatePackageInput,
} from '../../services/package.service';
import { parsePackageMetadata, PackageWithParsedMetadata } from '../../types/admin';
import { PackageCard } from '../../components/admin/PackageCard';
import { EmptyState } from '../../components/admin/EmptyState';
import { useNotification } from '../../hooks/useNotification';
import { PackageFormDialog, PackageFormData } from '../../components/admin/CreatePackageDialog';

// ============================================
// PackageManagement Component
// ============================================

const trimToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed || null;
};

export const PackageManagement: React.FC = () => {
  // Hooks
  const notification = useNotification();
  const { closeSnackbar } = useSnackbar();
  const { data: packages, isLoading, error } = usePackages();
  const toggleStatusMutation = useTogglePackageStatus();
  const createPackageMutation = useCreatePackage();
  const updatePackageMutation = useUpdatePackage();

  // State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedPackage, setSelectedPackage] = useState<PackageFormData | undefined>(undefined);

  // Parse metadata for all packages
  const packagesWithParsedMetadata = useMemo<PackageWithParsedMetadata[]>(() => {
    if (!packages) return [];
    return packages.map((pkg) => ({
      ...pkg,
      metadata: parsePackageMetadata(pkg.metadata || '{}'),
    }));
  }, [packages]);

  const updatePackageStatus = async (publicId: string, isActive: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({ publicId, isActive });
      notification.success(`Package ${isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      notification.error('Failed to update package status');
    }
  };

  // Handle toggle status
  const handleToggleStatus = (publicId: string, isActive: boolean) => {
    const pkg = packagesWithParsedMetadata.find((item) => item.publicId === publicId);

    if (!isActive) {
      notification.warning(
        `Deactivate ${pkg?.name ?? 'this package'}? New subscriptions will stop, active users will move to FREE, and unused package credits will be carried over.`,
        {
          key: `deactivate-package-${publicId}`,
          persist: true,
          action: (snackbarId) => (
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={() => {
                closeSnackbar(snackbarId);
                void updatePackageStatus(publicId, false);
              }}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Deactivate
            </Button>
          ),
        }
      );
      return;
    }

    void updatePackageStatus(publicId, isActive);
  };

  // Handle edit
  const handleEdit = (publicId: string) => {
    const pkg = packagesWithParsedMetadata.find((p) => p.publicId === publicId);
    if (!pkg) {
      notification.error('Package not found');
      return;
    }

    // Transform package data to form data
    const formData: PackageFormData = {
      publicId: pkg.publicId,
      code: pkg.code,
      name: pkg.name,
      descriptionEn: pkg.descriptionEn ?? '',
      descriptionVi: pkg.descriptionVi ?? '',
      highlightsEn: pkg.highlightsEn ?? '',
      highlightsVi: pkg.highlightsVi ?? '',
      price: pkg.price,
      priceVnd: pkg.priceVnd,
      maxAgents: pkg.maxAgents,
      tokensPerMonth: pkg.tokensPerMonth,
      advanceTokensPerMonth: pkg.advanceTokensPerMonth,
      duration: pkg.duration,
      isActive: pkg.isActive,
      allowDynamicTheme: pkg.allowDynamicTheme,
      features: pkg.features || [],
      featureLimits: pkg.featureLimits || {},
      featureDailyLimits: pkg.featureDailyLimits || {},
      integrationEntitlements: pkg.integrationEntitlements || [],
      integrationLimits: pkg.integrationLimits || {},
    };

    setSelectedPackage(formData);
    setDialogMode('edit');
    setIsDialogOpen(true);
  };

  // Handle create package
  const handleCreatePackage = () => {
    setSelectedPackage(undefined);
    setDialogMode('create');
    setIsDialogOpen(true);
  };

  // Handle form submit (create or edit)
  const handleFormSubmit = async (data: PackageFormData) => {
    try {
      if (dialogMode === 'create') {
        const createData: CreatePackageInput = {
          code: data.code.trim().toUpperCase(),
          name: data.name,
          descriptionEn: trimToNull(data.descriptionEn),
          descriptionVi: trimToNull(data.descriptionVi),
          highlightsEn: trimToNull(data.highlightsEn),
          highlightsVi: trimToNull(data.highlightsVi),
          price: data.price,
          priceVnd: data.priceVnd,
          maxAgents: data.maxAgents,
          tokensPerMonth: data.tokensPerMonth,
          advanceTokensPerMonth: data.advanceTokensPerMonth,
          duration: data.duration,
          isActive: data.isActive,
          allowDynamicTheme: data.allowDynamicTheme,
          features: data.features,
          featureLimits: data.featureLimits,
          featureDailyLimits: data.featureDailyLimits,
          integrationEntitlements: data.integrationEntitlements,
          integrationLimits: data.integrationLimits,
        };
        await createPackageMutation.mutateAsync(createData);
        notification.success('Package created successfully');
      } else {
        const updateData: UpdatePackageInput = {
          publicId: data.publicId!,
          name: data.name,
          descriptionEn: trimToNull(data.descriptionEn),
          descriptionVi: trimToNull(data.descriptionVi),
          highlightsEn: trimToNull(data.highlightsEn),
          highlightsVi: trimToNull(data.highlightsVi),
          price: data.price,
          priceVnd: data.priceVnd,
          maxAgents: data.maxAgents,
          tokensPerMonth: data.tokensPerMonth,
          advanceTokensPerMonth: data.advanceTokensPerMonth,
          duration: data.duration,
          isActive: data.isActive,
          allowDynamicTheme: data.allowDynamicTheme,
          features: data.features,
          featureLimits: data.featureLimits,
          featureDailyLimits: data.featureDailyLimits,
          integrationEntitlements: data.integrationEntitlements,
          integrationLimits: data.integrationLimits,
        };
        await updatePackageMutation.mutateAsync(updateData);
        notification.success('Package updated successfully');
      }
      setIsDialogOpen(false);
      setSelectedPackage(undefined);
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        `Failed to ${dialogMode === 'create' ? 'create' : 'update'} package`;
      notification.error(errorMessage);
    }
  };

  return (
    <Box className="h-full bg-slate-50 p-8 dark:bg-slate-900">
      {/* Page Header */}
      <Box className="mb-6 flex items-start justify-between">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Package Management
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Manage subscription packages, token quotas, and pricing
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={handleCreatePackage}
          sx={{
            backgroundColor: '#3B82F6',
            '&:hover': {
              backgroundColor: '#2563EB',
            },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Create New Package
        </Button>
      </Box>

      {/* Content Area */}
      {isLoading ? (
        // Loading State
        <Box className="flex h-[400px] items-center justify-center">
          <CircularProgress size={40} className="text-blue-500" />
        </Box>
      ) : error ? (
        // Error State
        <EmptyState
          Icon={PackageIcon}
          title="Failed to load packages"
          description="Please try again later"
        />
      ) : packagesWithParsedMetadata.length === 0 ? (
        // Empty State
        <EmptyState
          Icon={PackageIcon}
          title="No packages available"
          description="Get started by creating your first package"
          actionLabel="Create your first package"
          onAction={handleCreatePackage}
        />
      ) : (
        // Package Grid
        <Grid container spacing={3}>
          {packagesWithParsedMetadata.map((pkg) => (
            <Grid key={pkg.publicId} xs={12} sm={6} md={4} lg={3}>
              <PackageCard package={pkg} onToggleStatus={handleToggleStatus} onEdit={handleEdit} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Package Form Dialog (Create/Edit) */}
      <PackageFormDialog
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedPackage(undefined);
        }}
        onSubmit={handleFormSubmit}
        isLoading={
          dialogMode === 'create'
            ? createPackageMutation.isPending
            : updatePackageMutation.isPending
        }
        mode={dialogMode}
        initialData={selectedPackage}
      />
    </Box>
  );
};
